import { useCallback, useState } from 'react';

import Button from 'components/shared/Button';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/flexible/Profile';
import { DenialListImport } from 'services/api/admin/denialListImports';
import PlusIcon from 'svg/PlusIcon';

import DenialListImportTracker from './DenialListImportTracker';
import UploadDenialListModal from './UploadDenialListModal';

/**
 * Owns the upload flow for the Global Denial Lists tab: the entry-point button, the upload modal,
 * and the per-import processing/success/failure notifications (which outlive the modal). Lives in
 * the tab's headerExtra so the notifications persist after the modal closes.
 *
 * View Only Olio Admins lack adminDenialListCreate and never see the button, keeping the tab
 * read-only for them.
 */
export default function UploadDenialListButton() {
  const { profile } = useProfile();
  const [modalOpen, setModalOpen] = useState(false);
  const [trackedImports, setTrackedImports] = useState<DenialListImport[]>([]);

  const handleUploaded = useCallback((created: DenialListImport) => {
    setTrackedImports((prev) => [...prev, created]);
  }, []);

  const handleSettled = useCallback((importId: string) => {
    setTrackedImports((prev) => prev.filter((i) => i.id !== importId));
  }, []);

  if (!profile.has(PERMISSIONS.adminDenialListCreate)) return null;

  return (
    <>
      <Button variant='primary' onClick={() => setModalOpen(true)}>
        <PlusIcon width={14} height={14} />
        Upload Denial List
      </Button>

      {modalOpen && <UploadDenialListModal onClose={() => setModalOpen(false)} onUploaded={handleUploaded} />}

      {trackedImports.map((imp) => (
        <DenialListImportTracker key={imp.id} denialListImport={imp} onSettled={handleSettled} />
      ))}
    </>
  );
}
